import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useEffect, useCallback } from 'react';
import { X, Save } from 'lucide-react';
export const EditProjectModal = ({ isOpen, project, onClose, onSave }) => {
    const [projectName, setProjectName] = useState('');
    const [projectDescription, setProjectDescription] = useState('');
    const [errors, setErrors] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    // Initialize form when project changes
    useEffect(() => {
        if (project) {
            setProjectName(project.name);
            setProjectDescription(project.description);
            setErrors({});
        }
    }, [project]);
    // Reset form when modal closes
    useEffect(() => {
        if (!isOpen) {
            setProjectName('');
            setProjectDescription('');
            setErrors({});
            setIsSaving(false);
        }
    }, [isOpen]);
    // Form validation (same as CreateProjectModal)
    const validateForm = useCallback(() => {
        const newErrors = {};
        if (!projectName.trim()) {
            newErrors.projectName = 'Project name is required';
        }
        if (!projectDescription.trim()) {
            newErrors.projectDescription = 'Project description is required';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }, [projectName, projectDescription]);
    // Clear errors when user types
    const handleProjectNameChange = (value) => {
        setProjectName(value);
        if (errors.projectName && value.trim()) {
            setErrors(prev => ({ ...prev, projectName: undefined }));
        }
    };
    const handleProjectDescriptionChange = (value) => {
        setProjectDescription(value);
        if (errors.projectDescription && value.trim()) {
            setErrors(prev => ({ ...prev, projectDescription: undefined }));
        }
    };
    const handleSave = useCallback(async () => {
        if (!validateForm() || !project)
            return;
        setIsSaving(true);
        try {
            await onSave(project.id, projectName.trim(), projectDescription.trim());
            // Modal will be closed by parent component
        }
        catch (error) {
            console.error('Failed to update project:', error);
            // In dummy implementation, we don't show error UI, just log
        }
        finally {
            setIsSaving(false);
        }
    }, [validateForm, project, projectName, projectDescription, onSave]);
    const handleCancel = useCallback(() => {
        if (!isSaving) {
            onClose();
        }
    }, [isSaving, onClose]);
    // Handle ESC key
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && isOpen && !isSaving) {
                onClose();
            }
        };
        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown);
            return () => document.removeEventListener('keydown', handleKeyDown);
        }
    }, [isOpen, isSaving, onClose]);
    if (!isOpen)
        return null;
    return (_jsxs("div", { className: "fixed inset-0 flex items-center justify-center z-50", children: [_jsx("div", { className: "ls-modal-backdrop", onClick: () => {
                    // Allow clicking backdrop to close modal (if not saving)
                    if (!isSaving) {
                        onClose();
                    }
                } }), _jsxs("div", { className: "ls-modal-card ls-modal-card-animate relative max-w-md w-full mx-4 flex flex-col", style: { maxHeight: '92vh' }, onClick: (e) => e.stopPropagation(), children: [_jsxs("div", { className: "flex items-center justify-between p-6 ls-modal-band-top shrink-0", style: { backgroundColor: 'var(--forest)' }, children: [_jsx("h3", { className: "text-lg font-semibold", style: { color: 'var(--sand)' }, children: "Edit Project" }), _jsx("button", { onClick: handleCancel, disabled: isSaving, className: "p-1 rounded hover:bg-opacity-20 hover:bg-white transition-colors", style: { color: 'var(--sand)' }, children: _jsx(X, { size: 20 }) })] }), _jsxs("div", { className: "p-6 space-y-4 overflow-y-auto min-h-0 flex-1", style: { backgroundColor: 'var(--sand)' }, children: [_jsxs("div", { children: [_jsx("label", { htmlFor: "editProjectName", className: "block text-sm font-medium mb-2", style: { color: 'var(--charcoal)' }, children: "Project Name" }), _jsx("input", { id: "editProjectName", type: "text", value: projectName, onChange: (e) => handleProjectNameChange(e.target.value), placeholder: "Enter project name", disabled: isSaving, className: `w-full px-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-opacity-50 ${errors.projectName
                                            ? 'border-red-500'
                                            : ''}`, style: {
                                            backgroundColor: 'white',
                                            borderColor: errors.projectName ? '#ef4444' : 'var(--pewter)',
                                            color: 'var(--charcoal)'
                                        } }), errors.projectName && (_jsx("p", { className: "text-red-500 text-sm mt-1", children: errors.projectName }))] }), _jsxs("div", { children: [_jsx("label", { htmlFor: "editProjectDescription", className: "block text-sm font-medium mb-2", style: { color: 'var(--charcoal)' }, children: "Project Description" }), _jsx("textarea", { id: "editProjectDescription", value: projectDescription, onChange: (e) => handleProjectDescriptionChange(e.target.value), placeholder: "Enter project description", disabled: isSaving, rows: 3, className: `w-full px-3 py-2 rounded-lg border text-sm resize-none focus:outline-none focus:ring-2 focus:ring-opacity-50 ${errors.projectDescription
                                            ? 'border-red-500'
                                            : ''}`, style: {
                                            backgroundColor: 'white',
                                            borderColor: errors.projectDescription ? '#ef4444' : 'var(--pewter)',
                                            color: 'var(--charcoal)'
                                        } }), errors.projectDescription && (_jsx("p", { className: "text-red-500 text-sm mt-1", children: errors.projectDescription }))] })] }), _jsxs("div", { className: "flex justify-end space-x-3 p-6 ls-modal-band-bottom shrink-0", style: { backgroundColor: 'var(--pewter)' }, children: [_jsx("button", { onClick: handleCancel, disabled: isSaving, className: "px-4 py-2 text-sm rounded-lg border transition-colors", style: {
                                    borderColor: 'var(--sand)',
                                    color: 'var(--sand)',
                                    backgroundColor: 'transparent'
                                }, children: "Cancel" }), _jsx("button", { onClick: handleSave, disabled: isSaving, className: "px-4 py-2 text-sm rounded-lg transition-colors flex items-center space-x-2", style: {
                                    backgroundColor: 'var(--forest)',
                                    color: 'var(--sand)'
                                }, children: isSaving ? (_jsxs(_Fragment, { children: [_jsx("div", { className: "animate-spin rounded-full h-4 w-4 border-b-2 border-white" }), _jsx("span", { children: "Saving..." })] })) : (_jsxs(_Fragment, { children: [_jsx(Save, { size: 16 }), _jsx("span", { children: "Save Changes" })] })) })] })] })] }));
};
